import { useHistory } from 'react-router-dom'
import { useStore, useDispatch } from 'react-redux'
import { Box, Button, IconButton, Tooltip } from '@mui/material'
import MultiLangLabel from '../../Component/common/label/MultiLangLabel'
import MUITable from '../../Component/common/table/MUITable'
import { ListFilter } from '../../Component/ListDisplay/ListFilter'
import Iconify from '../../Component/common/iconify/Iconify'
import { ICONIFY } from '../../Constants/iconify'
import { Add } from '@mui/icons-material'
import SurveyDialog from '../../Component/Flow/survey/SurveyDialog'
import { setSurveyFlow } from '../../actions/surveyFlowAction'
import { constants } from '../../Constants/constants'
import { KEYS_OF_IS_CREATE, PREFIX_OF_TEMP_NODE } from '../../Component/Flow/constant'
import StatusLabel from '../../Component/_v2/label/StatusChip'
import { useSurveyList } from '../../hooks/useSurveyList'
import { useSurveyDraft } from '../../hooks/useSurveyDraft'
import { useDialog } from '../../hooks/useDialog'
import { surveyService } from '../../services/surveyService'


export default function SurveyList({
  newPath,
  editBtn = true,
  createBtn = true,
  cloneBtn = true,
  onClickListRow,
  readOnly = false,
}) {
  const dispatch = useDispatch()
  const history = useHistory()
  const store = useStore()

  // Filter definitions
  const surveyListFilters = [
    {
      className: 'filter-input',
      label: { resource: 'surveyPage', key: 'title' },
      clear: true,
      value: '',
      name: 'title',
      size: constants.SIZE.SMALL,
    },
    {
      className: 'filter-input',
      label: { resource: 'surveyPage', key: 'remarks' },
      clear: true,
      value: '',
      name: 'remarks',
      size: constants.SIZE.SMALL,
    },
    {
      className: 'filter-input',
      label: { resource: 'surveyPage', key: 'status' },
      value: '',
      type: constants.TYPE.SELECT,
      selectValue: constants.CHOICES.BRAND.STATUS,
      name: 'status',
      size: constants.SIZE.SMALL,
    },
  ]

  // Custom hooks for state management
  const {
    list,
    listLength,
    loading,
    pagination,
    filters,
    updateFilters,
    changePage,
    changeRowsPerPage,
    refetch,
  } = useSurveyList(surveyListFilters)

  const { clearDraft } = useSurveyDraft()
  const {
    open: createDialogOpen,
    openDialog: openCreateDialog,
    closeDialog: closeCreateDialog,
  } = useDialog()

  // Table columns
  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 200,
      flex: 1,
    },
    {
      field: 'title',
      headerName: 'Title',
      width: 200,
      flex: 1,
      renderCell: (params) => (
        <MultiLangLabel
          nameObj={params.row.title}
          direction='vertical'
          gap={0.5}
          itemWidth='100%'
        />
      ),
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      width: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 50,
      renderCell: (params) => <StatusLabel resource='survey' keyName={params.value} />,
    },
    ...(!readOnly
      ? [
          {
            field: 'edit',
            headerName: '',
            width: 50,
            renderCell: (params) =>
              editBtn ? (
                <Tooltip title='edit'>
                  <IconButton onClick={() => onClickEdit(params.row.id)}>
                    <Iconify icon={ICONIFY.edit} />
                  </IconButton>
                </Tooltip>
              ) : null,
          },
          {
            field: 'clone',
            headerName: '',
            width: 50,
            renderCell: (params) =>
              cloneBtn ? (
                <Tooltip title='clone'>
                  <IconButton onClick={() => onClickClone(params.row.id)}>
                    <Iconify icon={ICONIFY.clone} />
                  </IconButton>
                </Tooltip>
              ) : null,
          },
        ]
      : []),
  ]

  // Event handlers
  const onClickCreate = () => {
    dispatch(setSurveyFlow(constants.SURVEY_FLOW))
    openCreateDialog()
  }

  const onClickEdit = (id) => {
    history.push(`/${constants.PATH.SURVEY.BASE}/` + id)
  }

  const onClickClone = async (id) => {
    try {
      await surveyService.clone(id, store)
      refetch()
    } catch (error) {
      console.error('Clone failed:', error)
    }
  }

  const handleCreateSubmit = (data) => {
    dispatch(setSurveyFlow({ ...constants.SURVEY_FLOW, ...data }))
    history.push(
      `/${constants.PATH.SURVEY.BASE}/` +
        `${PREFIX_OF_TEMP_NODE}${Date.now()}?${KEYS_OF_IS_CREATE}=true`
    )
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* Title and Create Button */}
        {!onClickListRow && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className='title'>Survey</div>
            <Button startIcon={<Add />} variant='contained' onClick={onClickCreate}>
              Create
            </Button>
          </Box>
        )}

        {/* Filters */}
        <ListFilter onChangeListFilters={updateFilters} listFilters={filters} />

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <MUITable
            columns={columns}
            rows={list}
            pagination={pagination}
            listLength={listLength}
            onClickListRow={onClickListRow}
            handleChangePage={(e, newPage) => changePage(newPage)}
            handleChangeRowsPerPage={(e) => changeRowsPerPage(e?.target?.value)}
            loading={loading}
          />
        </Box>
      </Box>

      {/* Create Dialog */}
      <SurveyDialog
        isEdit={false}
        open={createDialogOpen}
        toggleOpen={closeCreateDialog}
        handleSubmit={handleCreateSubmit}
      />
    </>
  )
}
